/**
 * Group-period picker: org grouping + nested indentation (Page Permissions Manager pattern).
 */
import { APP_SLUGS } from '../constants/appSlugs';

export const SYSTEM_GROUP_NAME = 'System';

export async function buildGroupsPeriodsHierarchyMeta(allGpRows, fetchFn) {
  const orgRows = (allGpRows || []).filter(
    (gp) => String(gp.group_type || '').toLowerCase() === 'organization'
  );
  const systemRows = (allGpRows || []).filter(
    (gp) => String(gp.group_type || '').toLowerCase() === 'system'
  );
  const nextMeta = {};

  orgRows.forEach((org) => {
    nextMeta[String(org.id)] = {
      organization: org.group_name || 'Organization',
      parentGpId: null,
      depth: 0
    };
  });
  systemRows.forEach((sys) => {
    nextMeta[String(sys.id)] = {
      organization: SYSTEM_GROUP_NAME,
      parentGpId: null,
      depth: 0
    };
  });

  const loadNested = async (rootGp, organizationLabel) => {
    try {
      const r = await fetchFn(
        `/api/relationships/nested-groups?superparent_groups_periods_id=${encodeURIComponent(rootGp.id)}`
      );
      const d = await r.json();
      return {
        organizationLabel,
        groups: d.success && Array.isArray(d.data) ? d.data : []
      };
    } catch {
      return { organizationLabel, groups: [] };
    }
  };

  const nestedResults = await Promise.all([
    ...orgRows.map((org) => loadNested(org, org.group_name || 'Organization')),
    ...systemRows.map((sys) => loadNested(sys, SYSTEM_GROUP_NAME))
  ]);

  nestedResults.forEach(({ organizationLabel, groups }) => {
    groups.forEach((g) => {
      (g.periods || []).forEach((p) => {
        nextMeta[String(p.id)] = {
          organization: organizationLabel,
          parentGpId: p.parent_groups_periods_id || g.parent_groups_periods_id || null,
          depth: null
        };
      });
    });
  });

  const depthMemo = {};
  const depthOf = (gpId, guard = new Set()) => {
    const id = String(gpId || '');
    if (!id) return 0;
    if (depthMemo[id] != null) return depthMemo[id];
    if (guard.has(id)) return 0;
    const row = nextMeta[id];
    if (!row || !row.parentGpId) {
      depthMemo[id] = row?.depth ?? 0;
      return depthMemo[id];
    }
    const nextGuard = new Set(guard);
    nextGuard.add(id);
    const parentDepth = depthOf(row.parentGpId, nextGuard);
    depthMemo[id] = parentDepth + 1;
    return depthMemo[id];
  };
  Object.keys(nextMeta).forEach((id) => {
    nextMeta[id].depth = depthOf(id);
  });

  return nextMeta;
}

export function getGpOrganizationLabel(gp, byId, hierarchyMeta) {
  if (!gp) return 'Other / System';
  const meta = hierarchyMeta?.[String(gp.id)];
  if (meta?.organization) return String(meta.organization);
  if (gp.organization_name) return String(gp.organization_name);
  const visited = new Set();
  let cur = gp;
  while (cur && !visited.has(String(cur.id))) {
    visited.add(String(cur.id));
    if (String(cur.group_type || '').toLowerCase() === 'organization') {
      return String(cur.group_name || 'Organization');
    }
    const parentGpId = cur.parent_groups_periods_id ? String(cur.parent_groups_periods_id) : '';
    if (!parentGpId) break;
    cur = byId.get(parentGpId);
  }
  if (gp.parent_group_name && gp.parent_group_name !== SYSTEM_GROUP_NAME) {
    return String(gp.parent_group_name);
  }
  return 'Other / System';
}

/** Organization / system root gp (superparent target for org-level app assignments). */
export function isOrgRootGroupsPeriod(gp, hierarchyMeta = {}) {
  if (!gp) return false;
  const type = String(gp.group_type || '').toLowerCase();
  if (type !== 'organization' && type !== 'system') return false;
  const meta = hierarchyMeta[String(gp.id)];
  if (meta && Number.isFinite(meta.depth)) return meta.depth === 0;
  return meta?.parentGpId == null;
}

export function getAssignGpDisabledReason({ gp, appSlug, hierarchyMeta, assignedGpIdsForApp }) {
  if (!gp) return 'Invalid group-period.';
  const id = String(gp.id);
  if (assignedGpIdsForApp?.has(id)) {
    return 'This app is already assigned to this group-period.';
  }
  const slug = String(appSlug || '').toLowerCase();
  if (slug === APP_SLUGS.WORK_SAMPLES && !isOrgRootGroupsPeriod(gp, hierarchyMeta)) {
    return 'Work Samples must be assigned on the organization group-period (org root), not a nested team.';
  }
  return null;
}

export function groupGroupsPeriodsForPicker({
  groupsPeriods = [],
  hierarchyMeta = {},
  search = ''
}) {
  const q = String(search || '').trim().toLowerCase();
  const byId = new Map((groupsPeriods || []).map((gp) => [String(gp.id), gp]));
  const visible = (groupsPeriods || []).filter((gp) => {
    if (!q) return true;
    const org = getGpOrganizationLabel(gp, byId, hierarchyMeta);
    return `${org} ${gp.group_name || ''} ${gp.period_title || ''} ${gp.period_id || ''} ${gp.group_type || ''}`
      .toLowerCase()
      .includes(q);
  });

  const map = new Map();
  visible.forEach((gp) => {
    const org = getGpOrganizationLabel(gp, byId, hierarchyMeta);
    if (!map.has(org)) map.set(org, []);
    map.get(org).push(gp);
  });

  const orderInOrganization = (items) => {
    const ids = new Set(items.map((gp) => String(gp.id)));
    const children = new Map();
    const roots = [];
    const keyLabel = (gp) =>
      `${gp.group_name || ''} ${gp.period_title || gp.period_id || ''}`.toLowerCase();
    const compare = (a, b) => keyLabel(a).localeCompare(keyLabel(b));

    const parentIdFor = (gp) => {
      const direct = gp.parent_groups_periods_id ? String(gp.parent_groups_periods_id) : '';
      if (direct && ids.has(direct)) return direct;
      const meta = hierarchyMeta?.[String(gp.id)];
      const metaParent = meta?.parentGpId ? String(meta.parentGpId) : '';
      return metaParent && ids.has(metaParent) ? metaParent : '';
    };

    items.forEach((gp) => {
      const parentId = parentIdFor(gp);
      if (!parentId) {
        roots.push(gp);
        return;
      }
      if (!children.has(parentId)) children.set(parentId, []);
      children.get(parentId).push(gp);
    });

    roots.sort(compare);
    children.forEach((arr) => arr.sort(compare));

    const depthFromMeta = (gp, fallbackDepth) => {
      const meta = hierarchyMeta?.[String(gp.id)];
      return Number.isFinite(meta?.depth) ? meta.depth : fallbackDepth;
    };

    const out = [];
    const visit = (gp, depth, guard = new Set()) => {
      const id = String(gp.id);
      if (guard.has(id)) return;
      const nextGuard = new Set(guard);
      nextGuard.add(id);
      out.push({ ...gp, _depth: depthFromMeta(gp, depth) });
      (children.get(id) || []).forEach((child) => visit(child, depth + 1, nextGuard));
    };
    roots.forEach((gp) => visit(gp, 0));

    const seen = new Set(out.map((x) => String(x.id)));
    items
      .filter((gp) => !seen.has(String(gp.id)))
      .sort(compare)
      .forEach((gp) => out.push({ ...gp, _depth: depthFromMeta(gp, 0) }));

    return out;
  };

  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
    .map(([organization, items]) => ({
      organization,
      items: orderInOrganization(items)
    }))
    .filter((group) => group.items.length > 0);
}

export function formatGroupsPeriodPickerLabel(gp) {
  const period = gp.period_title || gp.period_id || '';
  const type = String(gp.group_type || '').trim();
  const typeTag = type ? ` · ${type}` : '';
  return `${gp.group_name || 'Group'} — ${period || 'Period'}${typeTag}`;
}
